import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Rocket, CheckCircle2, Home, BookOpen, Tag, Sparkles, Plug, FileDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { submitToCustomerIO } from "@/utils/apiUtils";
import deepResearchPreview from "@/assets/onboarding-deep-research-preview.png";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { CityDataPreview } from "@/components/onboarding/CityDataPreview";

interface SummaryStepProps {
  count: number;
  onComplete: () => void;
  industryId?: string;
  selectedLabels?: string[];
  selectedKeys?: Set<string>;
}

export function SummaryStep({ count, onComplete, industryId, selectedLabels = [], selectedKeys }: SummaryStepProps) {
  const { toast } = useToast();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [cities, setCities] = useState("");
  const [userMessage, setUserMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const contextLine = `Try-it flow — Industry: ${industryId ?? "n/a"}. Priority cities: ${cities || "n/a"}. Selected ${count} data points: ${selectedLabels.slice(0, 50).join(", ")}${selectedLabels.length > 50 ? "…" : ""}`;
      const message = userMessage ? `${userMessage}\n\n---\n${contextLine}` : contextLine;
      await submitToCustomerIO({ email, name, company, message }, "demo-request");
      if ((window as any).gtag) {
        (window as any).gtag("event", "form_submit_success", {
          form_name: "Try It Demo Request",
          form_location: "/try-it",
        });
      }
      setSubmitted(true);
      toast({ title: "Thanks!", description: "We'll reach out shortly to schedule your demo." });
    } catch (err) {
      toast({
        title: "Something went wrong",
        description: err instanceof Error ? err.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8 pb-12 max-w-5xl mx-auto">
      <div className="text-center space-y-3 pt-6">
        <div className="rounded-full bg-primary/10 p-4 mx-auto w-fit">
          <Rocket className="h-9 w-9 text-primary" />
        </div>
        <h1 className="text-3xl font-bold tracking-tight">See your data</h1>
        <p className="text-lg text-muted-foreground">
          You've selected <span className="font-semibold text-foreground">{count} data points</span>.
          Here's a preview of what your data looks like in Fordje.
        </p>
      </div>

      {/* Personalized city preview */}
      <CityDataPreview industryId={industryId} selectedKeys={selectedKeys} />

      {/* Operator quote — bridges data → felt benefit */}
      <figure className="max-w-3xl mx-auto text-center px-4">
        <blockquote className="text-base sm:text-lg font-medium text-foreground/90 leading-snug">
          <span className="text-primary">“</span>What used to take a full week now takes an hour.<span className="text-primary">”</span>
        </blockquote>
        <figcaption className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
          Permitting Lead
        </figcaption>
      </figure>

      {/* Get it where you work — compact two-up strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex items-start gap-4 rounded-xl border bg-muted/30 px-5 py-4 shadow-sm">
          <div className="rounded-lg bg-primary/10 p-2.5 shrink-0">
            <Plug className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold">Connect to your stack</div>
            <p className="text-sm text-muted-foreground leading-snug mt-0.5">
              Live data in your CRM, design, and permitting tools.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-4 rounded-xl border bg-muted/30 px-5 py-4 shadow-sm">
          <div className="rounded-lg bg-[hsl(38,92%,50%)]/10 p-2.5 shrink-0">
            <FileDown className="h-5 w-5 text-[hsl(38,92%,50%)]" />
          </div>
          <div className="min-w-0">
            <div className="text-base font-semibold">Download a report</div>
            <p className="text-sm text-muted-foreground leading-snug mt-0.5">
              Clean, shareable AHJ report for partners, project folders, or clients.
            </p>
          </div>
        </div>
      </div>

      {/* Deep Research teaser — compact */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-background to-background p-4 sm:p-5">
        <div className="grid gap-4 md:grid-cols-2 items-center">
          <div className="space-y-2">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              <Sparkles className="h-3 w-3" />
              Deep Research
            </div>
            <h2 className="text-lg sm:text-xl font-bold tracking-tight">
              Need to dig into the details?
            </h2>
            <p className="text-sm text-muted-foreground">
              Ask in plain language. Get cited answers from every official document that matters.
            </p>
          </div>
          <div>
            <div className="relative rounded-lg border bg-background shadow-md overflow-hidden ring-1 ring-border/50 max-w-md mx-auto md:ml-auto md:mr-0">
              <div className="flex items-center gap-1 border-b bg-muted/40 px-2 py-1">
                <span className="h-1.5 w-1.5 rounded-full bg-red-400/70" />
                <span className="h-1.5 w-1.5 rounded-full bg-yellow-400/70" />
                <span className="h-1.5 w-1.5 rounded-full bg-green-400/70" />
              </div>
              <img
                src={deepResearchPreview}
                alt="Preview of Fordje Deep Research interface for Miami's codes"
                className="block w-full h-auto"
              />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-background to-transparent" />
            </div>
          </div>
        </div>
      </div>

      {/* Demo form */}
      <div className="max-w-2xl mx-auto w-full pt-4">
        <div className="rounded-xl border bg-card p-6 sm:p-8 shadow-sm">
          {submitted ? (
            <div className="flex flex-col items-center text-center gap-4 py-6">
              <CheckCircle2 className="h-10 w-10 text-primary" />
              <h3 className="text-xl font-semibold">You're on the list</h3>
              <p className="text-sm text-muted-foreground">
                We'll reach out shortly to walk you through your full dataset. In the meantime:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full mt-2">
                <Button asChild variant="outline" className="justify-center gap-2">
                  <Link to="/"><Home className="h-4 w-4" /> Home</Link>
                </Button>
                <Button asChild variant="outline" className="justify-center gap-2">
                  <Link to="/blog"><BookOpen className="h-4 w-4" /> Insights</Link>
                </Button>
                <Button asChild variant="outline" className="justify-center gap-2">
                  <Link to="/pricing"><Tag className="h-4 w-4" /> Pricing</Link>
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="text-center mb-6">
                <h3 className="text-2xl sm:text-[1.7rem] font-bold tracking-tight mb-2">
                  Book a demo
                </h3>
                <p className="text-sm text-muted-foreground max-w-md mx-auto">
                  We'll show your {count} data points live across your cities.
                </p>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ti-name">Full Name</Label>
                    <Input id="ti-name" value={name} onChange={(e) => setName(e.target.value)} required placeholder="Your name" maxLength={100} />
                  </div>
                  <div>
                    <Label htmlFor="ti-email">Work Email</Label>
                    <Input id="ti-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@company.com" maxLength={100} />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="ti-company">Company</Label>
                    <Input id="ti-company" value={company} onChange={(e) => setCompany(e.target.value)} required placeholder="Your company" maxLength={100} />
                  </div>
                  <div>
                    <Label htmlFor="ti-cities">Priority cities</Label>
                    <Input id="ti-cities" value={cities} onChange={(e) => setCities(e.target.value)} required placeholder="e.g. Phoenix, Austin, Denver" maxLength={250} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ti-message">Message <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <Textarea id="ti-message" value={userMessage} onChange={(e) => setUserMessage(e.target.value)} placeholder="Anything we should know?" maxLength={1000} rows={3} />
                </div>
                <Button type="submit" size="lg" className="w-full" disabled={loading}>
                  {loading ? "Submitting…" : "Book a Demo"}
                </Button>
              </form>
            </>
          )}
        </div>

        {!submitted && (
          <div className="mt-6 text-center text-sm text-muted-foreground">
            Not ready for a demo? Keep exploring{" "}
            <Link to="/" className="text-primary hover:underline font-medium">Home</Link>
            {", "}
            <Link to="/blog" className="text-primary hover:underline font-medium">Insights</Link>
            {", or "}
            <Link to="/pricing" className="text-primary hover:underline font-medium">Pricing</Link>
            .
          </div>
        )}
      </div>
    </div>
  );
}
